import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Features = () => {
  const features = [
    {
      number: 1,
      title: "Víte, co jíte a jste fit",
      description: "Konečně žádné odhadování kalorií, sacharidů ani tuků. Jen kontrola bez námahy. Už bude mnohem jednodušší dosáhnout tvého vysněného těla.",
      imageUrl: "/assets/stayFit.jpg"
    },
    {
      number: 2,
      title: "Jistota, že to nepřeháníte",
      description: "Kalori vám vždy ukáže, kdy jídlo ubrat a kdy si můžete dovolit více. Kompletně bez stresu a vše máte pod kontrolou.",
      imageUrl: "/assets/screenshot2.png"
    },
    {
      number: 3,
      title: "Už žádné vážení jídla",
      description: "Konec stresu. Jedna fotka nahradí celou tabulku, takže už žádné hodiny vážení.",
      imageUrl: "/assets/noWeight.jpg"
    },
    {
      number: 4,
      title: "Svátky přežijete bez přibírání",
      description: "Budete moct jíst cokoliv budete chtít a nestresovat se nad tím, že každou chvíli přiberete.",
      imageUrl: "/assets/vanoceBezPribirani.jpg"
    },
    {
      number: 5,
      title: "Vyfotíte jídlo, hned vidíte kalorie",
      description: "Jeden klik, bez psaní, bez tabulek. Stačí jen jedna fotka a máte na den vystaráno.",
      imageUrl: "/assets/mockupPizza.png"
    },
    {
      number: 6,
      title: "Každý den nové doporučené recepty",
      description: "Nevíte zrovna co si dát? Kalori vám každý den doporučí jídlo, které sedí do vašich cílů.",
      imageUrl: "/assets/doporucene.png"
    }
  ];

  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="relative px-4 py-16 md:py-24  overflow-hidden bg-gradient-to-b from-transparent to-green-50">
      {/* Decorative background elements */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-primary opacity-5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-primary-dark opacity-5 rounded-full blur-3xl"></div>
      
      <div className="relative max-w-5xl lg:max-w-7xl mx-auto ">
        {/* Section Title */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Co vás čeká uvnitř
          </h2>
          <div className="w-24 h-1 bg-gradient-primary mx-auto rounded-full"></div>
        </div>

        <div ref={ref} className="space-y-16 md:space-y-20">
          {features.map((feature, index) => {
            const isEven = index % 2 === 1;
            return (
              <div key={feature.number}>
                {/* Intermediate heading before feature 5 */}
                {index === 4 && (
                  <div className="mb-12 md:mb-16 text-center">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                      A to není vše...
                    </h3>
                  </div>
                )}
                
                {/* Feature Card */}
                <div
                  className={`flex flex-col items-center text-center lg:flex-row lg:items-center lg:gap-12 lg:text-left transition-all duration-400 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  } ${isEven ? 'lg:flex-row-reverse' : ''}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Feature Content */}
                  <div className="w-full max-w-3xl lg:flex-1 lg:max-w-none">
                    {/* Title */}
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
                      {feature.number}. {feature.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-base md:text-lg lg:text-xl text-gray-700 mb-8 lg:mb-0 leading-relaxed max-w-2xl mx-auto lg:max-w-none">
                      {feature.description}
                    </p>
                  </div>
                  
                  {/* Image or Placeholder */}
                  <div className="w-full max-w-2xl lg:flex-1 lg:max-w-none">
                    {feature.imageUrl ? (
                      <div className="relative w-full mx-auto h-56 sm:h-72 md:h-96 lg:h-80 xl:h-96 rounded-2xl overflow-hidden shadow-2xl group">
                        <img 
                          src={feature.imageUrl} 
                          alt={feature.title}
                          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary-dark/10 opacity-50 group-hover:opacity-75 transition-opacity duration-200"></div>
                      </div>
                    ) : (
                      <div className="relative w-full mx-auto h-56 sm:h-72 md:h-96 lg:h-80 xl:h-96 rounded-2xl overflow-hidden shadow-2xl group">
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300"></div>
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary-dark/10 opacity-50 group-hover:opacity-75 transition-opacity duration-200"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 rounded-full bg-white/40 backdrop-blur-sm flex items-center justify-center shadow-lg">
                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;

